import { MaybeRef, MaybeRefOrGetter } from "vue";

import { SiteQueryKeys } from "@/data/api/site-query-keys";
import { V4HSiteId } from "@/data/common/ids";
import { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";

export interface IPlanningOverridesByApplianceCategoryKeyParams {
  siteId: MaybeRef<number>;
  applianceCategory: EProgramApplianceCategory;
}

const query = {
  api: "kili",
  type: "query",
  scope: "sites",
} as const;

const siteQuery = (siteId: MaybeRefOrGetter<number>) => [...SiteQueryKeys.site(siteId), query] as const;
const siteSectionsQuery = (siteId: MaybeRefOrGetter<number>) => [...SiteQueryKeys.sections(siteId), query] as const;

export const SitesQueryKeys = {
  all: [query] as const,
  site: siteQuery,
  installedApplianceCategories: (siteId: MaybeRef<number>) =>
    [...siteQuery(siteId), { action: "find-installed-appliance-categories" }] as const,
  details: (siteId: MaybeRef<number>) => [...siteQuery(siteId), { action: "find-site-details" }] as const,
  anomalies: (siteId: MaybeRef<number>) => [...siteQuery(siteId), { action: "find-site-anomalies" }] as const,
  programs: (siteId: MaybeRef<number>) => [...siteQuery(siteId), { action: "find-programs" }] as const,
  programDetails: (siteId: MaybeRef<number>, programId: MaybeRef<number>) =>
    [...siteQuery(siteId), { action: "find-program-details", programId }] as const,
  planningOverrides: (siteId: MaybeRef<number>) =>
    [...siteQuery(siteId), { action: "find-planning-overrides" }] as const,
  planningOverridesByApplianceCategory: (params: IPlanningOverridesByApplianceCategoryKeyParams) =>
    [
      ...SitesQueryKeys.planningOverrides(params.siteId),
      {
        action: "find-planning-overrides-by-appliance-category",
        applianceCategory: params.applianceCategory,
      },
    ] as const,
  sectionsForest: (siteId: MaybeRef<V4HSiteId>) =>
    [...siteSectionsQuery(siteId), { action: "get-site-sections-forest" }] as const,
  sectionCategories: (siteId: MaybeRef<V4HSiteId>) =>
    [...siteSectionsQuery(siteId), { action: "get-site-section-categories" }] as const,
};

export const SitesMutationKeys = {
  all: [{ api: "kili", type: "mutation", scope: "sites" }] as const,
  siteSections: (siteId: MaybeRef<number>) =>
    [{ ...SitesMutationKeys.all[0], action: "search-site-sections", params: { siteId } }] as const,
  applyPlanningOverrides: () => [{ ...SitesMutationKeys.all[0], action: "apply-planning-overrides" }] as const,
};
